/**
 * Curated Unsplash imagery for marketing pages — single source of truth for cohesive look.
 * License: https://unsplash.com/license — Unsplash does not require attribution; we list
 * sources on /photo-credits for transparency.
 */

export const MARKETING_STOCK = {
  coupleHomeRelax: 'photo-1516589178581-6cd7833ae3b2',
  coupleOutdoor: 'photo-1529333166437-7750a6dd5a70',
  /** Prior id returned 404 from images.unsplash.com (removed/delisted). */
  coupleKitchen: 'photo-1556910103-1c02745aae4d',
  /** Prior id returned 404 from images.unsplash.com (removed/delisted). */
  handsWalking: 'photo-1529626455594-4ff0802cfb7e',
  supportCalm: 'photo-1573496359142-b8d87734a5a2',
  softAbstract: 'photo-1557683316-973673baf926',
} as const;

export type MarketingStockKey = keyof typeof MARKETING_STOCK;

/** Stable hotlink URL with size + quality. */
export function stockSrc(id: string, w: number, h?: number): string {
  const params = new URLSearchParams({
    w: String(w),
    q: '85',
    auto: 'format',
    fit: 'crop',
    ixlib: 'rb-4.0.3',
  });
  if (h != null) params.set('h', String(h));
  return `https://images.unsplash.com/${id}?${params.toString()}`;
}

export function stockUrl(key: MarketingStockKey, w: number, h?: number): string {
  return stockSrc(MARKETING_STOCK[key], w, h);
}

/** Public photo page on unsplash.com (hotlink path segment is `photo-{id}`). */
export function unsplashPhotoPageUrl(hotlinkId: string): string {
  const id = hotlinkId.startsWith('photo-') ? hotlinkId.slice('photo-'.length) : hotlinkId;
  return `https://unsplash.com/photos/${id}`;
}

/** Human-readable labels for the photo credits page (keys match MARKETING_STOCK). */
export const MARKETING_STOCK_CREDITS: ReadonlyArray<{ key: MarketingStockKey; label: string }> = [
  { key: 'coupleHomeRelax', label: 'Couple relaxing at home' },
  { key: 'coupleOutdoor', label: 'Couple outdoors' },
  { key: 'coupleKitchen', label: 'Couple in the kitchen' },
  { key: 'handsWalking', label: 'Couple together' },
  { key: 'supportCalm', label: 'Calm, professional support context' },
  { key: 'softAbstract', label: 'Soft abstract gradient' },
];
